import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import { Verification } from '../components/Verification.js';
import {
  Button,
  Card,
  Checkbox,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';

/** Uptime intervals offered; the plan sets the shortest (pricing §2). */
const INTERVALS = [60, 180, 300, 600, 900, 1800, 3600];
const MIN_INTERVAL: Record<string, number> = { free: 300, starter: 180, business: 60, agency: 60 };

type KeywordConfig = { keyword: string; mode: 'present' | 'absent'; case_sensitive: boolean };

function NameForm({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [name, setName] = useState(site.name);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PATCH('/sites/{siteId}', {
          params: { path: { siteId: site.id } },
          body: { name: name.trim() },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', site.id] }),
  });
  return (
    <Card>
      <h2 className="mb-3 text-xl font-semibold">{t('settings.general')}</h2>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <TextField
          label={t('settings.name')}
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          error={name.trim() ? undefined : t('errors.required')}
        />
        <p className="break-all text-slate-700">{site.url}</p>
        {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        <div>
          <Button type="submit" busy={save.isPending}>
            {t('app.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Monitoring({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const plan = useMe().data?.account.planCode ?? 'free';
  const http = site.monitors.find(
    (m) => m.type === 'http' && (m.url === null || m.url === site.url),
  );
  const refresh = () => client.invalidateQueries({ queryKey: ['site', site.id] });
  const pause = useMutation({
    mutationFn: (status: 'active' | 'paused') =>
      unwrap(
        api.PATCH('/sites/{siteId}', { params: { path: { siteId: site.id } }, body: { status } }),
      ),
    onSuccess: refresh,
  });
  const interval = useMutation({
    mutationFn: (seconds: number) =>
      unwrap(
        api.PATCH('/monitors/{monitorId}', {
          params: { path: { monitorId: http?.id ?? '' } },
          body: { intervalSeconds: seconds },
        }),
      ),
    onSuccess: refresh,
  });
  const min = MIN_INTERVAL[plan] ?? 300;
  const options = INTERVALS.filter((s) => s >= min);
  if (http && !options.includes(http.intervalSeconds)) options.push(http.intervalSeconds);
  const paused = site.status === 'paused';
  return (
    <Card>
      <h2 className="mb-3 text-xl font-semibold">{t('settings.monitoring')}</h2>
      <div className="flex flex-col gap-4">
        {http ? (
          <SelectField
            label={t('settings.interval')}
            hint={t('settings.intervalHint')}
            value={String(http.intervalSeconds)}
            onChange={(e) => interval.mutate(Number(e.target.value))}
            disabled={interval.isPending}
          >
            {options
              .sort((a, b) => a - b)
              .map((s) => (
                <option key={s} value={s}>
                  {s < 3600
                    ? t('settings.minutes', { count: s / 60 })
                    : t('settings.hours', { count: s / 3600 })}
                </option>
              ))}
          </SelectField>
        ) : null}
        {interval.isError ? <Notice kind="error">{errorMessage(interval.error, t)}</Notice> : null}
        <div>
          <p className="mb-2">{paused ? t('settings.pausedText') : t('settings.activeText')}</p>
          <Button
            variant="secondary"
            busy={pause.isPending}
            onClick={() => pause.mutate(paused ? 'active' : 'paused')}
          >
            {paused ? t('settings.resume') : t('settings.pause')}
          </Button>
        </div>
        {pause.isError ? <Notice kind="error">{errorMessage(pause.error, t)}</Notice> : null}
      </div>
    </Card>
  );
}

function KeywordMonitors({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [url, setUrl] = useState('');
  const [mode, setMode] = useState<'present' | 'absent'>('present');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [error, setError] = useState<string>();
  const refresh = () => client.invalidateQueries({ queryKey: ['site', site.id] });
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/monitors', {
          params: { path: { siteId: site.id } },
          body: {
            type: 'keyword',
            ...(url.trim() ? { url: url.trim() } : {}),
            config: { keyword: keyword.trim(), mode, case_sensitive: caseSensitive },
          },
        }),
      ),
    onSuccess: async () => {
      setKeyword('');
      setUrl('');
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (monitorId: string) =>
      unwrap(api.DELETE('/monitors/{monitorId}', { params: { path: { monitorId } } })),
    onSuccess: refresh,
  });
  const plan = useMe().data?.account.planCode;
  const keywords = site.monitors.filter((m) => m.type === 'keyword');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!keyword.trim()) return setError(t('errors.required'));
    setError(undefined);
    add.mutate();
  };
  let failure: string | undefined;
  if (add.error instanceof ApiError && add.error.code === 'PLAN_LIMIT_REACHED')
    failure = t('settings.keywords.limit', { max: add.error.problem.limit?.max ?? 0 });
  else if (add.error) failure = errorMessage(add.error, t);

  return (
    <Card>
      <h2 className="mb-1 text-xl font-semibold">{t('settings.keywords.title')}</h2>
      <p className="mb-3 text-slate-700">{t('settings.keywords.intro')}</p>
      {keywords.length > 0 ? (
        <ul className="mb-4 flex flex-col gap-2">
          {keywords.map((m) => {
            const c = m.config as KeywordConfig;
            return (
              <li
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2"
              >
                <span className="min-w-0 break-all">
                  <strong>„{c.keyword}“</strong> {t(`settings.keywords.modes.${c.mode}`)}
                  <br />
                  <span className="text-sm text-slate-700">{m.url ?? site.url}</span>
                </span>
                <Button
                  variant="danger"
                  busy={remove.isPending && remove.variables === m.id}
                  onClick={() => remove.mutate(m.id)}
                  aria-label={t('settings.keywords.removeLabel', { keyword: c.keyword })}
                >
                  {t('settings.keywords.remove')}
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {plan === 'free' ? (
        <Notice kind="info">{t('settings.keywords.notInPlan')}</Notice>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-3">
          {failure ? <Notice kind="error">{failure}</Notice> : null}
          {remove.isError ? <Notice kind="error">{errorMessage(remove.error, t)}</Notice> : null}
          <TextField
            label={t('settings.keywords.keyword')}
            value={keyword}
            maxLength={200}
            onChange={(e) => setKeyword(e.target.value)}
            error={error}
          />
          <TextField
            label={t('settings.keywords.url')}
            hint={t('settings.keywords.urlHint')}
            optional
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <SelectField
            label={t('settings.keywords.mode')}
            value={mode}
            onChange={(e) => setMode(e.target.value as 'present' | 'absent')}
          >
            <option value="present">{t('settings.keywords.modes.present')}</option>
            <option value="absent">{t('settings.keywords.modes.absent')}</option>
          </SelectField>
          <Checkbox
            label={t('settings.keywords.caseSensitive')}
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
          />
          <div>
            <Button type="submit" busy={add.isPending}>
              {t('settings.keywords.add')}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function DeleteSite({ site }: { site: Schemas['SiteDetail'] }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () =>
      unwrap(api.DELETE('/sites/{siteId}', { params: { path: { siteId: site.id } } })),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['sites'] });
      void navigate('/');
    },
  });
  return (
    <Card className="border-red-200">
      <h2 className="mb-2 text-xl font-semibold">{t('settings.delete.title')}</h2>
      <p className="mb-3 text-slate-800">{t('settings.delete.text')}</p>
      {confirm ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate()}>
            {t('settings.delete.confirm', { name: site.name })}
          </Button>
          <Button variant="secondary" onClick={() => setConfirm(false)}>
            {t('app.cancel')}
          </Button>
        </div>
      ) : (
        <Button variant="danger" onClick={() => setConfirm(true)}>
          {t('settings.delete.button')}
        </Button>
      )}
      {remove.isError ? (
        <div className="mt-3">
          <Notice kind="error">{errorMessage(remove.error, t)}</Notice>
        </div>
      ) : null}
    </Card>
  );
}

/** Site settings: name, pause and interval, keyword monitors, ownership, deletion. */
export function SiteSettings() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const site = useQuery({
    queryKey: ['site', siteId, 'detail'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}', { params: { path: { siteId } } })),
  });
  if (site.isPending) return <Loading />;
  if (site.isError)
    return <LoadError message={errorMessage(site.error, t)} onRetry={() => void site.refetch()} />;
  return (
    <>
      <PageTitle>{t('settings.title')}</PageTitle>
      <div className="flex flex-col gap-6">
        <NameForm key={site.data.id} site={site.data} />
        <Monitoring site={site.data} />
        <KeywordMonitors site={site.data} />
        <Verification site={site.data} />
        <DeleteSite site={site.data} />
      </div>
    </>
  );
}
